/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */
import { useCallback } from 'react';
import { matchPath, useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RoutePath } from '../../shared/enums/RoutePath';
import useProcedure from '../../procedure/useProcedure';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';
import { useUserAccounts } from '../../redux/features/user/userAccounts/useUserAccounts';
import { toggleRightMenu } from '../../redux/features/submission/submission.slice';
import { getValidationsThunk } from '../../redux/features/submission/submission.thunk.validations';
import {
  postOnSectionChangeThunk,
  postSendToSignAndPackThunk,
  putUserSignatureThunk,
} from '../../redux/features/submission/submission.thunk.signing';
import {
  selectSubmissionData,
  selectSubmissionIsRightMenuOpen,
  selectSubmissionUiSchema,
  selectSubmissionValidationErrors,
  selectSubmissionWorkflowValidations,
} from '../../redux/features/submission/submission.selectors';
import { useAttachmentsConfig } from '../../components/draft/attachments/hooks/useAttachmentsConfig';

export const useSigningForm = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const procedure = useProcedure();
  const { user, currentOrganization } = useUserAccounts();

  const isSigningPage = () => !!matchPath(location?.pathname, RoutePath.PROCEDURE_SIGNING_SUBMISSION.path);
  const { submissionId } = useParams();

  // fetch configuration elements
  const { resource: validationSchema } = useConfiguration(
    ConfigResourceProps.SUBMISSION_SIGNING_VALIDATION_SCHEMA,
    procedure?.id,
  );
  const { resource: _uiSchema } = useConfiguration(ConfigResourceProps.SUBMISSION_SIGNING_UI_SCHEMA, procedure?.id);
  const submission = useSelector(selectSubmissionData);
  const validatedUiSchema = useSelector(selectSubmissionUiSchema);
  const workflowValidations = useSelector(selectSubmissionWorkflowValidations);
  const validationErrors = useSelector(selectSubmissionValidationErrors);
  const isRightMenuOpen = useSelector(selectSubmissionIsRightMenuOpen);

  const uiSchema = validatedUiSchema ?? _uiSchema;

  // preload attachments config
  useAttachmentsConfig([]);

  const onSave = () => dispatch(putUserSignatureThunk({ uiSchema }));
  const onSaveAndClose = () => dispatch(putUserSignatureThunk({ isSaveAndClose: true }));
  const onClose = useCallback(async () => history.push('/'), [history]);

  const onSendToPackage = () => {
    dispatch(postSendToSignAndPackThunk());
  };

  const onValidate = ({ formData, errors }, controller) => {
    dispatch(
      getValidationsThunk({
        submission: {
          ...formData,
        },
        validationErrors: errors,
        uiSchema,
        t,
        controller,
      }),
    ).catch((error) => {
      console.warn(error);
    });
  };

  const onSectionChange = ({ formData, changeProps }) => {
    dispatch(postOnSectionChangeThunk({ formData, changeProps, uiSchema }));
  };

  return {
    procedure,
    submission,
    submissionId,
    validationSchema,
    uiSchema,
    workflowValidations,
    errors: validationErrors,
    onSave,
    onSaveAndClose,
    onClose,
    onSendToPackage,
    onValidate,
    onSectionChange,
    isRightMenuOpen,
    toggleRightMenu: () => dispatch(toggleRightMenu()),
    isSigningPage,
    user,
    currentOrganization,
  };
};
